/* =============================================================================
   VIEWS / ADMIN / RESERVATIONS-CONFIRM — Confirm-/Conflict-/Commit-/Preorder-Flow
   =============================================================================
   S69 REF-3 Cut 3/5: aus reservations.js (1.429 LOC) extrahiert. Komplexester
   Block — komplette Bestätigungs-Pipeline inkl. Vorbestellungs-Bridge.

   Enthält:
     - confirmReservation(id)              — Picker öffnen (intent='confirm')
     - changeAssignedTables(id)            — Picker öffnen (intent='change')
     - mapTriggerError(err)         (priv) — DB-Fehler → UI-Modal-Spec
     - renderConfirmConflictModal()  (priv)— Konflikt-Modal mit Alternativ-Zeiten
     - reopenAssignmentPicker(id)          — Re-Open aus Konflikt-Modal
     - applyConflictAlternative(id, t)     — Auf Alternativ-Zeit verschieben
     - autoCreateOrderFromReservation(r)   — Vorbestellung → Küchen-Order
                                             (auch von bulk konsumiert!)
     - manuallyCreateOrderForReservation() — Retry-Button im Detail-Modal
     - commitAssignmentAndConfirm()        — Submit-Lock + Inner-Call-Wrapper
     - _commitAssignmentAndConfirmInner()  — Vorprüfung + Update + Events
     - openReservationPreorderPrompt()     — Modal „jetzt Vorbestellung?"
     - _ensureReservationPreorderBridges() — window-Bridges für Modal-onclicks

   Cross-Module-Calls (via window):
     - window.openReservationDetail (actions) — von manuallyCreateOrderForRes
     - window.openAdminOrderEntry (admin-order-entry) — von Preorder-Bridge
     - window.openAssignmentPicker (reservation-engine) — von confirm/change
============================================================================ */

import {
  htmlEscape, fmtDate, timeToMinutes, showToast,
  preorderHasItems
} from '../../utils/helpers.js';
import { closeModal, rerenderActiveTab } from '../../utils/dom.js';
import {
  getReservationDuration,
  violatesClosingTime,
  occupiedSeatsInWindow,
  totalCapacity,
  suggestAlternativeTimes
} from '../../core/capacity.js';
import { getState } from './reservations-helpers.js';

export function confirmReservation(id) {
  if (typeof window.openAssignmentPicker === 'function') {
    window.openAssignmentPicker(id, 'confirm');
  }
}

export function changeAssignedTables(id) {
  if (typeof window.openAssignmentPicker === 'function') {
    window.openAssignmentPicker(id, 'change');
  }
}

/**
 * Mappt eine rohe Postgres-Trigger-/Constraint-Fehlermeldung auf eine
 * UI-freundliche Variante. Wird vom Last-Resort-catch in
 * `commitAssignmentAndConfirm` genutzt, falls die clientseitige Vorprüfung
 * eine Race-Condition mit einem parallelen Insert verfehlt hat.
 */
export function mapTriggerError(err) {
  const raw = (err && (err.message || err.details || String(err))) || '';
  if (/Ladenschluss|opening|closing.*time|enforce_reservation_end_within_opening/i.test(raw)) {
    return {
      type: 'closing_time',
      title: 'Reservierung würde nach Ladenschluss enden',
      message: 'Diese Reservierung läuft über die Schließzeit hinaus. '
             + 'Bitte eine frühere Uhrzeit wählen oder die Reservierungs-Dauer kürzen.',
      raw
    };
  }
  if (/Kapazität|capacity|enforce_reservation_capacity|enforce_reservation_simultaneous_capacity/i.test(raw)) {
    return {
      type: 'capacity',
      title: 'Kapazität für diese Uhrzeit reicht nicht',
      message: 'Mit dieser Bestätigung würde die simultane Gästezahl das '
             + 'verfügbare Platzangebot überschreiten. Du kannst eine andere '
             + 'Zeit wählen, andere Tische zuweisen oder die Reservierung absagen.',
      raw
    };
  }
  return {
    type: 'unknown',
    title: 'Bestätigung fehlgeschlagen',
    message: raw || 'Unbekannter Fehler beim Speichern der Bestätigung.',
    raw
  };
}

/**
 * Rendert ein Konflikt-Modal anstelle der unhandled-Promise-Rejection von vor
 * Session 8.2 (F-01). Enthält freundliche Erklärung, bis zu sechs Alternativ-
 * Zeit-Vorschläge am selben Tag und Buttons für „andere Tische wählen" oder
 * „abbrechen". Die rohe DB-Fehlermeldung bleibt aufklappbar — als Debug-Hilfe,
 * nicht als primäre UI.
 */
export function renderConfirmConflictModal(r, mapped) {
  if (!r) {
    showToast(mapped.title + ': ' + mapped.message, 'error');
    return;
  }
  const showAlternatives = (mapped.type === 'capacity' || mapped.type === 'closing_time');
  // S272-B: Alternativen mit der Dauer DIESER Reservierung prüfen (nicht dem
  // Standard) und die Reservierung selbst aus dem Sweep halten — sonst bot das
  // Modal 17:00 an, obwohl die 120-Min-Reservierung 18:00–19:00 voll lief
  // (Server lehnte ab, S271-Retest 4.4).
  const alts = showAlternatives
    ? suggestAlternativeTimes(r.date, r.time, r.guests, r.area || 'any', 6,
        { durationMin: getReservationDuration(r), excludeId: r.id })
    : [];
  const altHtml = showAlternatives
    ? (alts.length
        ? `<div class="picker-intro">Diese Zeiten am ${fmtDate(r.date)} hätten Platz für ${r.guests} ${r.guests === 1 ? 'Person' : 'Personen'}:</div>
           <div class="ap-alternatives-grid" style="margin-bottom:16px;">
             ${alts.map(a => `
               <button class="ap-alt" type="button"
                       onclick="applyConflictAlternative('${r.id}', '${a.time}')"
                       title="${a.seatsAvailable} Plätze frei">
                 <span class="ap-alt-time">${a.time}</span>
                 <span class="ap-alt-seats">${a.seatsAvailable} frei</span>
               </button>
             `).join('')}
           </div>`
        : `<p style="color: var(--ink-muted); font-size: .9rem; margin: 8px 0 16px;">An diesem Tag ist keine andere Zeit verfügbar.</p>`)
    : '';
  const detailHtml = mapped.raw
    ? `<details style="margin-top: 12px;">
         <summary style="cursor:pointer; color:var(--ink-muted); font-size:.85rem;">Details aus der Datenbank</summary>
         <pre style="background:var(--bg-soft); padding:8px; font-size:.78rem; white-space:pre-wrap; word-wrap:break-word; margin-top:4px;">${htmlEscape(mapped.raw)}</pre>
       </details>`
    : '';
  const html = `
    <div class="modal-header">
      <h2>${htmlEscape(mapped.title)}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="picker-meta">
      <strong>${htmlEscape(r.name)}</strong> · ${r.guests} ${r.guests === 1 ? 'Person' : 'Personen'}
      &nbsp;·&nbsp; ${fmtDate(r.date)} um ${r.time} Uhr
    </div>
    <div class="picker-warn"
         style="background:var(--danger-soft); color:var(--danger); padding:12px 14px; border-radius:6px; margin: 14px 0;">
      ⚠ ${htmlEscape(mapped.message)}
    </div>
    ${altHtml}
    <div class="btn-row picker-actions">
      <button class="btn btn-secondary" type="button" onclick="reopenAssignmentPicker('${r.id}')">Andere Tische wählen</button>
      <button class="btn btn-ghost" type="button" onclick="closeModal()">Abbrechen</button>
    </div>
    ${detailHtml}
  `;
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (content) content.innerHTML = html;
  if (overlay) overlay.classList.add('active');
}

/** Re-öffnet den Assignment-Picker — wird aus dem Konflikt-Modal aufgerufen. */
export function reopenAssignmentPicker(id) {
  if (typeof window.openAssignmentPicker === 'function') {
    window.openAssignmentPicker(id, 'confirm');
  }
}

/**
 * Übernimmt eine vorgeschlagene Alternativ-Zeit aus dem Konflikt-Modal:
 * verschiebt die Reservierung auf die neue Zeit und öffnet anschließend
 * den Assignment-Picker neu, damit der Admin frische Tisch-Vorschläge sieht.
 */
export async function applyConflictAlternative(id, newTime) {
  if (!id || !newTime) return;
  try {
    await window.App.storage.reservations.update(id, { time: newTime });
    showToast(`Auf ${newTime} Uhr verschoben — bitte Tische erneut auswählen.`, 'success');
    closeModal();
    if (typeof window.openAssignmentPicker === 'function') {
      // Kurz warten, bis Cache + UI durch sind, dann Picker neu öffnen.
      setTimeout(() => window.openAssignmentPicker(id, 'confirm'), 50);
    }
  } catch (err) {
    console.error('[applyConflictAlternative]', err);
    showToast('Verschieben fehlgeschlagen: ' + (err.message || err), 'error');
  }
}

/**
 * Phase 17 (Session 22) — Vorbestellung→Küche-Brücke.
 *
 * Wird nach erfolgreichem Reservierungs-Confirm aufgerufen (Single +
 * Bulk-Pfad). Wenn die Reservierung eine non-empty preorder.items hat,
 * ruft sie das RPC create_order_from_reservation auf — das legt eine
 * 'confirmed' dine-in-Order an, sodass KDS+Bar die Vorbestellung sehen.
 *
 * Idempotenz steckt im RPC: bei mehrfachem Aufruf für dieselbe
 * Reservation wird die existing Order zurückgegeben (wasExisting=true).
 *
 * Fehler hier dürfen den Reservierungs-Confirm NICHT crashen — sie
 * werden geloggt und als Toast angezeigt, aber die Reservation bleibt
 * confirmed. Der Bediener kann dann manuell aus der Detail-Card
 * nachfeuern (Button kommt in Phase 17 Schritt 5).
 *
 * S69 REF-3: explizit exportiert (Memory `gg-module-extract`).
 */
export async function autoCreateOrderFromReservation(reservation) {
  if (!reservation) return null;
  if (!preorderHasItems(reservation.preorder)) return null;
  try {
    const result = await window.App.storage.orders.createFromReservation(reservation.id);
    if (result) {
      if (window.App.events) {
        await window.App.events.emit('order:created', result);
      }
      if (result.wasExisting) {
        showToast(`📋 Vorbestellung bereits in der Küche (Order ${result.orderNumber || ''})`, 'info');
      } else {
        showToast(`📋 Vorbestellung an Küche übergeben (Order ${result.orderNumber || ''})`, 'success');
      }
    }
    return result;
  } catch (err) {
    console.error('[autoCreateOrderFromReservation]', err);
    showToast('Vorbestellung konnte nicht an Küche übergeben werden: ' + (err.message || err), 'error');
    return null;
  }
}

/**
 * Phase 17 (Session 22) — manuelle Nachfeuerung der Vorbestellung→Küche-
 * Übergabe aus der Reservierungs-Detail-Card. Wird vom "Jetzt an Küche
 * senden"-Button gerufen, wenn die Auto-Übergabe im Confirm-Flow nicht
 * lief (RPC-Fehler, fehlende Berechtigung) oder die Verlinkung verloren
 * ging. Idempotenz steckt im RPC; ein erneuter Klick erzeugt keine
 * Zweit-Order. Nach Erfolg wird die Detail-Card neu geöffnet, damit der
 * Bediener den neuen Kitchen-Info-Block sieht.
 *
 * S69 REF-3: ruft window.openReservationDetail (reservations-actions.js).
 */
export async function manuallyCreateOrderForReservation(id) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  if (!r) return;
  if (!preorderHasItems(r.preorder)) {
    showToast('Diese Reservierung hat keine Vorbestellung', 'info');
    return;
  }
  const result = await autoCreateOrderFromReservation(r);
  if (result) {
    // Card neu öffnen, damit der grüne "In der Küche"-Block erscheint
    closeModal();
    setTimeout(() => {
      if (typeof window.openReservationDetail === 'function') {
        window.openReservationDetail(id);
      }
    }, 120);
  }
}

// S29 KRITISCH-Fix: Submit-Lock auf Admin-Confirm gegen Doppel-Klick.
// Im Funktionstest hat Jonas 5× Bestätigungs-Mail bekommen — der Confirm-
// Modal-Button war noch klickbar, während der DB-Update lief, und jeder
// Klick feuerte ein eigenes `reservation:confirmed`-Event. Lock pro (id,
// intent), damit z.B. „Bestätigen" und „Zuweisen aktualisieren" sich
// nicht gegenseitig blockieren.
const _commitInFlight = new Set();

export async function commitAssignmentAndConfirm(id, tables, chairsUsed, intent) {
  const lockKey = `${id}:${intent || 'commit'}`;
  if (_commitInFlight.has(lockKey)) {
    console.info('[commitAssignmentAndConfirm] bereits in Flight — Doppelklick ignoriert:', lockKey);
    return;
  }
  _commitInFlight.add(lockKey);

  // Modal-Buttons disablen, damit der User visuell sieht: läuft.
  const modalRoot = document.getElementById('modal-content');
  const disabledButtons = [];
  if (modalRoot) {
    modalRoot.querySelectorAll('button:not([disabled])').forEach(b => {
      b.disabled = true;
      disabledButtons.push(b);
    });
  }

  try {
    return await _commitAssignmentAndConfirmInner(id, tables, chairsUsed, intent);
  } finally {
    _commitInFlight.delete(lockKey);
    disabledButtons.forEach(b => { b.disabled = false; });
  }
}

async function _commitAssignmentAndConfirmInner(id, tables, chairsUsed, intent) {
  const state = getState();
  const r = state.reservations.find(x => x.id === id);
  const chairs = Number(chairsUsed) || 0;
  const changes = {
    assignedTables: tables.slice(),
    tablesCombined: tables.length > 1,
    needsExtraChair: chairs > 0,
    extraChairsUsed: chairs
  };
  if (intent === 'confirm') {
    changes.status = 'confirmed';
    changes.confirmedAt = new Date().toISOString();
    // S89 (c): Confirm-Mail VERZÖGERN, wenn diese Reservierung (noch) keine
    // Vorbestellung hat — denn gleich kommt der „Vorbestellung aufnehmen?"-Prompt.
    // Der Trigger skippt dann (confirm_mail_held=true). Die Mail wird danach
    // nachgeholt: „Nein" → release-RPC (plain), „Ja" → Order-Trigger (with_order).
    // Hat die Reservierung schon eine Vorbestellung, kein Hold (with_order feuert
    // wie gehabt über den preorder_snapshot).
    if (r && !preorderHasItems(r.preorder)) {
      changes.confirmMailHeld = true;
    }
  }

  // -------------------------------------------------------------------
  // Vorprüfung clientseitig (Session 8.2 / F-01-Härtung).
  //
  // Greift NUR bei intent='confirm' — beim reinen Tisch-Wechsel
  // ('change') bleibt der Status unverändert, die DB-Trigger feuern
  // dafür ebenfalls nicht.
  //
  // Spiegelt das Muster aus walkin.js (submitWalkIn), damit Frontend,
  // Walk-In und Admin-Confirm dieselbe Sprache sprechen. Die DB-Trigger
  // bleiben Last-Resort für echte Race-Conditions (zwei Admins
  // gleichzeitig + paralleler Customer-RPC).
  // -------------------------------------------------------------------
  if (intent === 'confirm' && r) {
    const dur = getReservationDuration(r);
    const overrun = violatesClosingTime(r.time, dur);
    if (overrun) {
      closeModal();
      renderConfirmConflictModal(r, {
        type: 'closing_time',
        title: 'Reservierung würde nach Ladenschluss enden',
        message: `Reservierung würde um ${overrun.endStr} enden — `
               + `${overrun.overrunMin} Min nach Ladenschluss um ${overrun.closeStr}. `
               + `Bitte frühere Uhrzeit wählen oder die Reservierungs-Dauer kürzen.`,
        raw: ''
      });
      return;
    }
    const startMin = timeToMinutes(r.time);
    const endMin   = startMin + dur;
    // excludeId: diese Reservierung selbst aus der Sweep-Line raushalten,
    // sonst zählt sie doppelt sobald sie schon mal als 'confirmed' lief.
    // S271 §A — physisch: Reservierungen UND Walk-Ins (Spiegel des DB-Triggers).
    const currentMax = occupiedSeatsInWindow(r.date, startMin, endMin, r.id);
    const totalCap   = totalCapacity();
    if (currentMax + r.guests > totalCap) {
      closeModal();
      renderConfirmConflictModal(r, {
        type: 'capacity',
        title: 'Kapazität für diese Uhrzeit reicht nicht',
        message: `Mit dieser Bestätigung wären ${currentMax + r.guests} Gäste `
               + `simultan anwesend — über dem Maximum von ${totalCap} Plätzen. `
               + `Du kannst eine andere Zeit wählen oder andere Tische zuweisen.`,
        raw: ''
      });
      return;
    }
  }

  // -------------------------------------------------------------------
  // Eigentlicher Update-Call mit try/catch — der DB-Trigger als
  // Sicherheitsnetz fängt Race-Conditions ab. Vor der Härtung crashte
  // ein Trigger-Fehler den gesamten Confirm-Flow (unhandled-Promise-
  // Rejection, Modal blieb offen, keine Toast).
  // -------------------------------------------------------------------
  let updated;
  try {
    updated = await window.App.storage.reservations.update(id, changes);
  } catch (err) {
    console.error('[commitAssignmentAndConfirm] update failed:', err);
    const mapped = mapTriggerError(err);
    closeModal();
    renderConfirmConflictModal(r, mapped);
    return;
  }

  closeModal();
  if (intent === 'confirm') {
    // Funktionstest Audit-S21: KEINE direkten sendConfirmationEmail-Aufrufe
    // hier mehr! Der Event-Handler in bootstrap.js (App.events.on(
    // 'reservation:confirmed', ...)) ruft sendConfirmationEmail bereits
    // einmal — der vorherige Direkt-Aufruf hier produzierte Doppel-Mails
    // (Jonas bekam zwei Bestätigungs-Mails pro Confirm-Klick).
    await window.App.events.emit('reservation:confirmed', updated);
    // Phase 17 (Session 22): falls Vorbestellung vorhanden → Order in der
    // Küche/Theke erzeugen. Idempotent über reservation_id; Fehler hier
    // brechen den Confirm-Flow NICHT, sondern bleiben als Toast sichtbar.
    await autoCreateOrderFromReservation(updated);
    rerenderActiveTab();
    // S61 — Polish: Wenn die Reservation KEINE Vorbestellung hatte, fragen
    // wir den Operator nach dem Confirm direkt, ob er jetzt eine aufnehmen
    // möchte. Wenn die Reservation schon eine Vorbestellung hatte, übernimmt
    // autoCreateOrderFromReservation den Order-Pfad → kein zusätzliches Modal.
    if (!preorderHasItems(updated.preorder)) {
      setTimeout(() => openReservationPreorderPrompt(updated), 160);
    }
    return;
  } else {
    await window.App.events.emit('reservation:changed', { reservation: updated, fields: Object.keys(changes) });
    showToast('Tisch-Zuweisung aktualisiert', 'success');
  }
  rerenderActiveTab();
}

/** S61 — Modal nach Reservation-Confirm, das den Operator fragt, ob er
 *  jetzt direkt eine Bestellung für diese Reservation aufnehmen möchte.
 *  Praktisch z.B. wenn der Gast am Telefon noch eine Vorbestellung
 *  durchgibt, oder wenn die Reservation für HEUTE ist und der Service
 *  gleich beginnt.
 *
 *  Bei „Ja" öffnet sich der admin-order-entry-Pfad mit dem Reservation-
 *  Kontext (dine-in + erste assignedTables vorausgewählt). Die erzeugte
 *  Order wird über `orders.reservation_id` mit der Reservation verknüpft.
 */
export function openReservationPreorderPrompt(reservation) {
  const r = reservation;
  if (!r) return;
  const modalContent = document.getElementById('modal-content');
  if (!modalContent) return;

  // S61 — Bridge-Funktionen DIREKT vor Modal-Render registrieren, idempotent.
  // Verhindert, dass ein zuvor geleerter window-Hook (z.B. nach Reload-Cache-
  // Bug) das Modal-onclick auflaufen lässt.
  _ensureReservationPreorderBridges();
  const tables = (r.assignedTables || []).slice();
  const state = getState();
  const tableNames = tables.map(id => {
    const t = (state.tables || []).find(x => x.id === id);
    return t ? t.name : '?';
  }).join(' + ') || '–';
  const safeName = htmlEscape(r.name || 'Reservierung');
  const safeTime = htmlEscape(r.time || '');
  const safeDate = r.date ? fmtDate(r.date) : '';
  const html = `
    <div style="padding: 20px 24px 22px;">
      <div style="font-size:0.92rem; color:var(--ink-soft); margin-bottom:6px;">✓ Reservierung bestätigt</div>
      <h2 style="margin:0 0 8px 0; font-size:1.3rem;">${safeName}</h2>
      <div style="color:var(--ink-soft); font-size:0.92rem; margin-bottom:22px;">
        ${htmlEscape(safeDate)} um <strong>${safeTime}</strong> · ${r.guests} Personen · Tisch ${htmlEscape(tableNames)}
      </div>
      <div style="margin-bottom:20px; line-height:1.5;">
        <strong>Möchtest du jetzt eine Vorbestellung aufnehmen?</strong><br>
        <span style="font-size:0.88rem; color:var(--ink-soft);">
          Z.B. wenn der Gast am Telefon schon seine Bestellung durchgegeben hat.
        </span>
      </div>
      <div style="display:flex; gap:10px; justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="_declineReservationPreorder('${r.id}')">Nein, später</button>
        <button class="btn btn-primary" onclick="_openReservationPreorderEntry('${r.id}')">Ja, Vorbestellung aufnehmen</button>
      </div>
    </div>
  `;
  modalContent.innerHTML = html;
  // Modal-Overlay aktivieren — das styles.css-Pattern nutzt die `.active`-
  // Klasse, NICHT inline-style. closeModal() entfernt sie wieder.
  const overlay = document.getElementById('modal-overlay');
  if (overlay) overlay.classList.add('active');
}

/** Bridge-Funktionen: window-exposed weil die Modal-Buttons onclick="..."
 *  String-basiert sind und keinen Zugriff auf ES-Module-Imports haben.
 *
 *  S61 — Single-Pipeline-Garantie: Der „Ja, Vorbestellung aufnehmen"-Pfad
 *  delegiert an `openAdminOrderEntry` mit `reservationContext`. Das ist
 *  derselbe Code-Pfad, den auch der Walk-In-Order-Anlegungs-Workflow und
 *  die direkte „Bestellung aufnehmen"-Quick-Action nutzen — KEINE eigene
 *  Reservation-Order-Pipeline! Source-Tracking nur via
 *  `orders.reservation_id` Cross-Link nach Order-Create.
 *
 *  Idempotent: wird sowohl beim Modul-Load (unten) als auch direkt
 *  vor jedem Modal-Render (in openReservationPreorderPrompt) gerufen.
 */
function _ensureReservationPreorderBridges() {
  if (typeof window === 'undefined') return;

  window._closeReservationPreorderPrompt = function () {
    closeModal();
  };

  // S89 (c): „Nein, später" → die zuvor gehaltene schlanke Confirm-Mail jetzt
  // ausloesen (release-RPC loest den Hold + enqueued reservation_confirmed_customer).
  // Tolerant: Mail-Fehler brechen den Flow nicht (Reservierung ist bestätigt).
  window._declineReservationPreorder = function (reservationId) {
    closeModal();
    const supa = window.App && window.App.supabase;
    if (supa && typeof supa.rpc === 'function' && reservationId) {
      supa.rpc('release_reservation_confirmation_mail', { p_reservation_id: reservationId })
        .then(({ error }) => {
          if (error) console.warn('[reservation] release confirm-mail failed:', error.message || error);
        })
        .catch(e => console.warn('[reservation] release confirm-mail threw:', e));
    }
  };

  window._openReservationPreorderEntry = function (reservationId) {
    const st = getState();
    const r = (st.reservations || []).find(x => x.id === reservationId);
    if (!r) {
      showToast('Reservierung nicht gefunden', 'error');
      return;
    }
    closeModal();
    setTimeout(() => {
      if (typeof window.openAdminOrderEntry === 'function') {
        window.openAdminOrderEntry({
          serviceType: 'dine-in',
          reservationContext: {
            id: r.id,
            tableId: (r.assignedTables || [])[0] || null,
            assignedTables: (r.assignedTables || []).slice(),
            guests: r.guests,
            guestName: r.name || '',
            guestPhone: r.phone || '',
            customerId: r.customerId || null,
            notes: r.notes || '',
            // S61 — pickup-Zeit aus der Reservation übernehmen, damit die
            // Order als Pre-Order behandelt wird (KDS lila-Badge, scharf
            // erst 60 Min vor Reservation-Zeit, S60 FIX.3 Pre-Order-Window).
            date: r.date || null,
            time: r.time || null
          }
        });
      } else {
        showToast('Order-Entry-Modul nicht verfügbar — Cockpit neu laden.', 'error');
      }
    }, 140);
  };
}

/**
 * S111 — „Bestellung aufnehmen" direkt aus dem Reservierungs-Detail-Modal
 * (confirmed-Block). Dünner Wrapper OHNE den redundanten Ja/Nein-Prompt: der
 * Button IST schon das „Ja". Öffnet den Order-Entry mit Reservierungs-Kontext
 * (dine-in, `assignedTables` vorausgewählt, Order via `orders.reservation_id`
 * verknüpft) — exakt der Ja-Pfad aus `openReservationPreorderPrompt`
 * (`_openReservationPreorderEntry`), also dieselbe Single-Pipeline wie Walk-In/
 * Quick-Action. window-exportiert für den inline-onclick.
 */
export function takeReservationOrder(id) {
  _ensureReservationPreorderBridges();
  if (typeof window !== 'undefined' && typeof window._openReservationPreorderEntry === 'function') {
    window._openReservationPreorderEntry(id);
  }
}

// Modul-Load: Bridges einmalig registrieren (für Aufrufe vor erstem Modal).
_ensureReservationPreorderBridges();

// window-Bindings für inline-onclick-Konsumenten.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    confirmReservation, changeAssignedTables,
    reopenAssignmentPicker, applyConflictAlternative,
    commitAssignmentAndConfirm,
    manuallyCreateOrderForReservation,
    openReservationPreorderPrompt,
    takeReservationOrder
  });
}
